import {GrmMediaPipeInterface, GrmMediaPipeObject} from "@/lib/gooroomee/mediapipe/grm-mediapipe/GrmMediaPipeInterface";

/**
 * OffscreenCanvas 또는 DOM Canvas 생성
 * Worker 환경에서는 OffscreenCanvas만 사용 가능
 * 
 * @param {number} width - 캔버스 너비
 * @param {number} height - 캔버스 높이
 * @returns {OffscreenCanvas|HTMLCanvasElement} 생성된 캔버스
 * @throws {Error} Worker 환경에서 OffscreenCanvas 미지원 시
 */
function createOffscreenCanvas(width, height){
    // OffscreenCanvas 우선 사용 (Worker 안전)
    if(globalThis.OffscreenCanvas){
        return new OffscreenCanvas(width, height);
    }
    
    // Worker 환경 체크
    if(typeof document === 'undefined'){
        console.error('[createOffscreenCanvas] Worker environment detected but OffscreenCanvas not available');
        console.error('[createOffscreenCanvas] width:', width, 'height:', height);
        console.error('[createOffscreenCanvas] Stack:', new Error().stack);
        throw new Error('Cannot create canvas in Worker environment without OffscreenCanvas support');
    }
    
    // 메인 스레드: DOM Canvas 생성
    let cvs = document.createElement('canvas');
    Object.assign(cvs, {width, height});
    return cvs;
}

export default class GrmBlurFilter extends GrmMediaPipeInterface {
    _props = {}
    async init() {
        const ios = (()=>{
            const ua = navigator.userAgent;
            const isiPadOS =
                navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPadOS Desktop UA
            const isIOS = /iPhone|iPad|iPod/i.test(ua) || isiPadOS;
            const isIOSWebView =
                isIOS &&
                (
                    !/Version\/\d+/i.test(ua) ||
                    /(FBAN|FBAV|FB_IAB|Instagram|Line|KAKAOTALK|NAVER|DaumApps)/i.test(ua) ||
                    (typeof window !== 'undefined' && !!window?.webkit && !!window?.webkit?.messageHandlers)
                ) && !(navigator).standalone;

            let match = ua.match(/Version\/([\d.]+).*Safari/);
            if(match){
                return {
                    isIOS,
                    isSafari: true,
                    isIOSWebView,
                    version: parseFloat(match[1])
                };
            }
            return {
                isIOS,
                isIOSWebView,
                isSafari: false, version: null
            };
        })();

        // iOS Safari 17 이상: OffscreenCanvas + WebGL
        if((ios.isIOS || ios.isSafari) && !ios.isIOSWebView && ios.version >= 17){
            (() => {
                const cvs = createOffscreenCanvas(1, 1);
                const ctx = cvs.getContext('2d');
                this._props.tmpCtx = ctx;
                return ctx;
            })();
            
            const blurFilter = new WebGLBlurFilter();
            Object.assign(this._props, {
                blurFilter
            });
        }
        // iOS Safari 17 이하: 일반 canvas + WebGL
        else if((ios.isIOS || ios.isSafari) && (!ios.version || ios.version < 17)){
            // 2D 컨텍스트용 canvas (임시 작업용)
            const tmpCvs = document.createElement('canvas');
            Object.assign(tmpCvs, {width: 1, height: 1});
            const ctx = tmpCvs.getContext('2d');
            this._props.tmpCtx = ctx;
            
            // WebGL용 별도 canvas
            const webglCvs = document.createElement('canvas');
            Object.assign(webglCvs, {width: 1, height: 1});
            const blurFilter = new WebGLBlurFilter(webglCvs);
            Object.assign(this._props, {
                blurFilter
            });
        }
        // 기타: OffscreenCanvas + CSS filter
        else {
            (() => {
                const cvs = createOffscreenCanvas(1, 1);
                const ctx = cvs.getContext('2d');
                this._props.tmpCtx = ctx;
                return ctx;
            })();
        }
    }

    setOptions(options) {
        if(options.blurSize){
            options.blurSize = parseFloat(options.blurSize);
        }
        super.setOptions(options);
    }

    isIOSWebView(){
        const ua = navigator.userAgent || '';
        const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        const isIOS = /iPhone|iPad|iPod/i.test(ua) || isiPadOS;
        const hasSafariVersion = /Version\/\d+/i.test(ua);
        const isInApp = /(FBAN|FBAV|FB_IAB|Instagram|Line|KAKAOTALK|NAVER|DaumApps|GSA)/i.test(ua);
        const hasBridge = typeof window.webkit !== 'undefined' && !!window.webkit.messageHandlers;
        const isStandalone = (typeof navigator.standalone === 'boolean' && navigator.standalone) ||
            (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
        return isIOS && !isStandalone && (!hasSafariVersion || isInApp || hasBridge);
    }

    createCanvasCompat(width, height){
        const ua = navigator.userAgent || '';
        const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        const isIOS = /iPhone|iPad|iPod/i.test(ua) || isiPadOS;
        const hasSafariVersion = /Version\/\d+/i.test(ua);
        const inApp = /(FBAN|FBAV|FB_IAB|Instagram|Line|KAKAOTALK|NAVER|DaumApps|GSA)/i.test(ua);
        const hasBridge = (typeof window !== 'undefined') && !!window.webkit && !!window.webkit.messageHandlers;
        const isStandalone =
            (typeof navigator.standalone === 'boolean' && navigator.standalone) ||
            (typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
                window.matchMedia('(display-mode: standalone)').matches);

        const isIOSWebView = isIOS && !isStandalone && (!hasSafariVersion || inApp || hasBridge);

        if (isIOSWebView) {
            const c = document.createElement('canvas');
            c.width = width; c.height = height;
            return c;
        }
        if ('OffscreenCanvas' in globalThis) return new OffscreenCanvas(width, height);

        const c = document.createElement('canvas');
        c.width = width; c.height = height;
        return c;
    }

    _applyBlur ({ctx, x, y, width, height, blurSize}) {
        const {blurFilter, tmpCtx} = this._props;

        Object.assign(tmpCtx.canvas, {
            width, height
        })

        if(blurFilter){
            tmpCtx.drawImage(ctx.canvas, x, y, width, height, 0, 0, width, height);
            blurFilter.applyBlur(tmpCtx.canvas, blurSize);
            tmpCtx.drawImage(blurFilter.canvas, 0, 0, width, height);
        }else {
            try {
                tmpCtx.filter = `blur(${blurSize}px)`;
                tmpCtx.drawImage(ctx.canvas, x, y, width, height, 0, 0, width, height);
            } catch (_) {
                // CSS filter가 지원되지 않는 경우 아무 처리도 하지 않음
            } finally {
                // 다음 프레임에 영향 없도록 필터 초기화
                try {
                    tmpCtx.filter = 'none';
                } catch (_) {
                }
            }
            // iOS인 경우는 CamstudyPreview에서 div 오버레이로 처리됨
        }

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(tmpCtx.canvas, x, y, width, height);
    }

    async render({image, canvas, ctx}) {
        let {direction, percent, blurSize} = this.options;
        percent = parseFloat(percent);
        if(
            !direction
            || !blurSize
            || !percent
        ){
            return;
        }

        // const tmpCtx = this._props.tmpCtx || (() => {
        //     const cvs = new OffscreenCanvas(1, 1);
        //     const ctx = cvs.getContext('2d');
        //     this._props.tmpCtx = ctx;
        //     return ctx;
        // })();

        switch(direction){
            case 'top-bottom':
            case 'top-down':
                let x=0, y=0;
                let {width, height} = canvas;
                if(width <= 0 || height <= 0) return;

                height = parseInt(height *= percent);
                if(height <= 0) return;

                this._applyBlur({ctx, x, y, width, height, blurSize});
                break;
        }
    }
}

GrmMediaPipeObject.add('BlurFilter', GrmBlurFilter);

class WebGLBlurFilter {
    constructor(canvas) {
        this.canvas = canvas = canvas || createOffscreenCanvas(1, 1);
        this.gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (!this.gl) throw new Error('WebGL not supported');

        this.initBuffers();
        this.initFramebuffers();
        this.initShaders();
    }

    initBuffers() {
        const { gl } = this;

        this.positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1.0, -1.0,
            1.0, -1.0,
            -1.0,  1.0,
            1.0,  1.0
        ]), gl.STATIC_DRAW);

        this.uvBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            0.0, 0.0,  // 좌상단
            1.0, 0.0,  // 우상단
            0.0, 1.0,  // 좌하단
            1.0, 1.0   // 우하단
        ]), gl.STATIC_DRAW);
    }

    initFramebuffers() {
        const { gl } = this;

        this.framebuffer = gl.createFramebuffer();
        this.tempTexture = gl.createTexture();

        gl.bindTexture(gl.TEXTURE_2D, this.tempTexture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }

    initShaders() {
        const { gl } = this;

        const vertexShaderSource = `
            attribute vec2 position;
            attribute vec2 uv;
            varying vec2 v_uv;
            void main() {
                gl_Position = vec4(position, 0.0, 1.0);
                v_uv = uv;
            }
        `;

        const fragmentShaderSrc = (isHorizontal) => `
            precision mediump float;
            uniform sampler2D image;
            uniform float texelSize;
            uniform float weights[15];
            varying vec2 v_uv;
        
            void main() {
                vec2 dir = vec2(${isHorizontal ? 'texelSize' : '0.0'}, ${isHorizontal ? '0.0' : 'texelSize'});
                vec4 color = texture2D(image, v_uv) * weights[0];
                for (int i = 1; i < 15; i++) {
                    if (weights[i] == 0.0) break;
                    color += texture2D(image, v_uv + dir * float(i)) * weights[i];
                    color += texture2D(image, v_uv - dir * float(i)) * weights[i];
                }
                gl_FragColor = color;
            }
        `;

        const compileShader = (type, source) => {
            const shader = gl.createShader(type);
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error("Shader compile error:", type === gl.VERTEX_SHADER ? "VERTEX" : "FRAGMENT");
                console.error(gl.getShaderInfoLog(shader));
                console.error("Source:\n", source);
                return null;
            }
            return shader;
        };

        const makeProgram = (isHorizontal) => {
            const vs = compileShader(gl.VERTEX_SHADER, vertexShaderSource);
            const fs = compileShader(gl.FRAGMENT_SHADER, fragmentShaderSrc(isHorizontal));
            const program = gl.createProgram();
            gl.attachShader(program, vs);
            gl.attachShader(program, fs);
            gl.linkProgram(program);
            if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
                console.error(gl.getProgramInfoLog(program));
                return null;
            }
            return {
                program,
                attribs: {
                    position: gl.getAttribLocation(program, 'position'),
                    uv: gl.getAttribLocation(program, 'uv')
                },
                uniforms: {
                    texelSize: gl.getUniformLocation(program, 'texelSize'),
                    image: gl.getUniformLocation(program, 'image'),
                    weights: gl.getUniformLocation(program, 'weights')
                }
            };
        };

        this.programs = {
            horizontal: makeProgram(true),
            vertical: makeProgram(false)
        };
    }

    applyBlur(srcCanvas, blurRadius = 5) {
        const { gl, canvas } = this;
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

        canvas.width = srcCanvas.width;
        canvas.height = srcCanvas.height;

        // 텍스처 & 프레임버퍼 세팅
        gl.bindTexture(gl.TEXTURE_2D, this.tempTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, canvas.width, canvas.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.tempTexture, 0);

        // 소스 텍스처 업로드
        const srcTex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, srcTex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, srcCanvas);

        // 가우시안 weight 계산
        const weights = this.calcGaussianWeights(blurRadius);
        const paddedWeights = new Float32Array(15);
        weights.forEach((w, i) => paddedWeights[i] = w); // 나머지 0으로 유지

        // Horizontal Pass
        this.drawPass(this.programs.horizontal, srcTex, this.tempTexture, canvas.width, paddedWeights);

// Vertical Pass
        this.drawPass(this.programs.vertical, this.tempTexture, null, canvas.height, paddedWeights);

        gl.deleteTexture(srcTex);
    }

    drawPass(programInfo, inputTexture, outputTexture, resolution, weights) {
        const { gl } = this;
        gl.bindFramebuffer(gl.FRAMEBUFFER, outputTexture ? this.framebuffer : null);
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clear(gl.COLOR_BUFFER_BIT);

        gl.useProgram(programInfo.program);

        // Attribute 세팅
        gl.enableVertexAttribArray(programInfo.attribs.position);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.vertexAttribPointer(programInfo.attribs.position, 2, gl.FLOAT, false, 0, 0);

        gl.enableVertexAttribArray(programInfo.attribs.uv);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
        gl.vertexAttribPointer(programInfo.attribs.uv, 2, gl.FLOAT, false, 0, 0);

        // 텍스처 세팅
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, inputTexture);
        gl.uniform1i(programInfo.uniforms.image, 0);
        gl.uniform1f(programInfo.uniforms.texelSize, 1.0 / resolution);
        gl.uniform1fv(programInfo.uniforms.weights, weights);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    calcGaussianWeights(radius) {
        const sigma = radius;
        const kernelSize = Math.min(15, Math.floor(sigma * 3)); // sigma 기준으로 크기 증가
        const weights = new Array(kernelSize).fill(0);
        let total = 0;

        for (let i = 0; i < kernelSize; i++) {
            const x = i;
            const w = Math.exp(-0.5 * (x * x) / (sigma * sigma));
            weights[i] = w;
            total += i === 0 ? w : w * 2;
        }

        // 정규화
        for (let i = 0; i < kernelSize; i++) {
            weights[i] /= total;
        }

        return weights;
    }
}



/*
class WebGLBlurFilter2 {
    program;
    constructor(canvas) {
        this.canvas = canvas = canvas || createOffscreenCanvas(1, 1);
        this.gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (!this.gl) {
            throw new Error('WebGL not supported');
        }

        this.initShaders();
        this.initBuffers();
    }

    initBuffers() {
        const {gl} = this;

        // 전체 화면을 덮는 사각형의 정점 좌표
        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1.0, -1.0,
            1.0, -1.0,
            -1.0,  1.0,
            1.0,  1.0
        ]), gl.STATIC_DRAW);

        // UV 좌표 버퍼 (상하 반전)
        const uvBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            0.0, 1.0,  // 좌하단
            1.0, 1.0,  // 우하단
            0.0, 0.0,  // 좌상단
            1.0, 0.0   // 우상단
        ]), gl.STATIC_DRAW);

        this.positionBuffer = positionBuffer;
        this.uvBuffer = uvBuffer;
    }

    initShaders() {
        const vertexShaderSource = `
            attribute vec2 position;
            attribute vec2 uv;
            varying vec2 v_uv;
            void main() {
                gl_Position = vec4(position, 0.0, 1.0);
                v_uv = uv;
            }
        `;

        const fragmentShaderSource = `
            precision mediump float;
            uniform sampler2D image;
            uniform float blurSize;
            varying vec2 v_uv;

            const int BLUR_RADIUS = 40;

            void main() {
                vec4 color = vec4(0.0);
                float total = 0.0;

                // const mediump int BLUR_RADIUS = int(blurSize);  // blurSize에 따라 반지름을 동적으로 설정

                for (int x = -BLUR_RADIUS; x <= BLUR_RADIUS; x++) {
                    for (int y = -BLUR_RADIUS; y <= BLUR_RADIUS; y++) {
                        // 2D 가우시안 분포 계산
                        float weight = exp(-(float(x*x + y*y)) / (2.0 * blurSize * blurSize));
                        vec2 offset = vec2(float(x), float(y)) / vec2(512.0, 512.0);
                        color += texture2D(image, v_uv + offset) * weight;
                        total += weight;
                    }
                }
                gl_FragColor = color / total;
            }
        `;

        const {gl} = this;

        function compileShader(type, source) {
            const shader = gl.createShader(type);
            if (!shader) {
                console.error('createShader fail!!: type[%s]', type);
                return null;
            }
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error(gl.getShaderInfoLog(shader));
                return null;
            }
            return shader;
        }

        const vertexShader = compileShader(gl.VERTEX_SHADER, vertexShaderSource);
        if (!vertexShader) {
            throw new Error('shader compile fail!! : VertexShader');
        }
        const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
        if (!vertexShader) {
            throw new Error('shader compile fail!! : FragmentShader');
        }
        const program = this.program = gl.createProgram();

        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(program));
            return null;
        }

        // Attribute 및 Uniform 위치 저장
        this.positionAttributeLocation = gl.getAttribLocation(program, 'position');
        this.uvAttributeLocation = gl.getAttribLocation(program, 'uv');
        this.blurSizeLocation = gl.getUniformLocation(program, 'blurSize');
        this.imageLocation = gl.getUniformLocation(program, 'image');
    }

    applyBlur(srcCanvas, blurRadius = 5) {
        const {gl} = this;
        if (!gl || !this.program) return;

        // 캔버스 크기 설정
        Object.assign(this.canvas, {
            width: srcCanvas.width,
            height: srcCanvas.height
        });

        // WebGL 상태 초기화
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        // 프로그램 활성화
        gl.useProgram(this.program);

        // 위치 attribute 설정
        gl.enableVertexAttribArray(this.positionAttributeLocation);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.vertexAttribPointer(this.positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

        // UV attribute 설정
        gl.enableVertexAttribArray(this.uvAttributeLocation);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
        gl.vertexAttribPointer(this.uvAttributeLocation, 2, gl.FLOAT, false, 0, 0);

        // 텍스처 생성 또는 업데이트
        if (!this.imageTexture) {
            this.imageTexture = gl.createTexture();
        }
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.imageTexture);

        // 텍스처 파라미터 설정
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        // 소스 캔버스의 이미지를 텍스처로 업로드
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, srcCanvas);

        // Uniform 설정
        if (this.blurSizeLocation) {
            gl.uniform1f(this.blurSizeLocation, blurRadius); // 블러 강도 스케일 조정
        }

        if (this.imageLocation) {
            gl.uniform1i(this.imageLocation, 0);  // 0번 텍스처 유닛 사용
        }

        // 그리기
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}
*/